import React, {useMemo, useState} from 'react';

import moment from 'moment-tz';

import {AccountPageShell} from 'app/account/components/AccountPageShell';
import {LinkPaginationRTK} from 'app/components/general/LinkPaginationRTK';
import {useCurrentUserState} from 'app/current-user/api';
import {ResetFilterMessage} from 'app/general/dashboards/ResetFilterMessage';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {
  useBulkDeleteRecycledResources,
  useBulkRestoreRecycledResources,
  useDeleteRecycledResource,
  useRecycledResources,
  useRestoreRecycledResource,
} from 'app/recycle-bin/api';
import {RecycleBinFilterbar} from 'app/recycle-bin/components/RecycleBinFilterbar';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {shallowEqual} from 'app/utils/react-utils';

import type {DashboardLocationParamsType} from 'app/documents/types';
import type {WithRouterProps} from 'react-router';
import type {
  ListResponseType,
  RecycledResourceSerializedType,
} from 'sweet-types';

export function RecycleBinPage({
  router,
  location,
}: WithRouterProps): JSX.Element {
  const {page, search, ordering, content_type} = location.query;

  const {data, error, isLoading} = useRecycledResources({
    page,
    page_size: DEFAULT_PAGE_SIZE,
    search,
    ordering,
    content_type,
  });

  function resetFilters() {
    return router.replace({pathname: location.pathname});
  }
  const viewingAll = useMemo(
    () => shallowEqual(Object.freeze({}), location.query),
    [location.query]
  );

  return (
    <AccountPageShell
      title={gt('Recycle Bin')}
      location="recycle"
      isLoading={isLoading}
      error={error}
      content={data}
      errorContent={{
        head: gt('Could not load the recycle bin at the moment'),
      }}
      noResultsContent={<EmptyBin />}
    >
      {(recycled_resources) => (
        <RecycleTable
          recycled_resources={recycled_resources}
          page={page}
          isLoading={isLoading}
          viewingAll={viewingAll}
          resetFilters={resetFilters}
          query={location.query}
        />
      )}
    </AccountPageShell>
  );
}

type RecycleTableProps = {
  recycled_resources: ListResponseType<RecycledResourceSerializedType>;
  page?: number;
  isLoading: boolean;
  viewingAll: boolean;
  query: DashboardLocationParamsType;
  resetFilters: () => void;
};

function RecycleTable({
  recycled_resources,
  page,
  isLoading,
  viewingAll,
  query,
  resetFilters,
}: RecycleTableProps) {
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [bulkRestoreRecycledResources] = useBulkRestoreRecycledResources();
  const [bulkDeleteRecycledResources] = useBulkDeleteRecycledResources();

  const {is_owner} = useCurrentUserState().data!;

  const numSelectedItems = useMemo(
    () => getUpdatedSelectedItems().length,
    [selectedItems, recycled_resources]
  );

  const isIndeterminateState = useMemo(
    () =>
      numSelectedItems !== recycled_resources.results.length &&
      numSelectedItems > 0,
    [selectedItems, recycled_resources]
  );

  const bulkMenuItems = [
    {
      text: gt('Restore (%(num)s selected)', {num: numSelectedItems}),
      key: 'bulk_restore',
      icon: 'far fa-trash-restore',
      disabled: numSelectedItems === 0,
      callback: () =>
        bulkRestoreRecycledResources({
          selectedItems: getUpdatedSelectedItems(),
        }),
      condition: () => true,
    },
    {
      text: gt('Permanently Delete (%(num)s selected)', {
        num: numSelectedItems,
      }),
      key: 'bulk_permanently_delete',
      icon: 'far fa-trash',
      disabled: numSelectedItems === 0,
      callback: () =>
        bulkDeleteRecycledResources({selectedItems: getUpdatedSelectedItems()}),
      condition: () => is_owner,
      className: numSelectedItems != 0 ? 'text-danger' : undefined,
    },
  ];

  function getUpdatedSelectedItems() {
    return selectedItems.filter((selectedItem) =>
      recycled_resources.results.some(
        (resource) => resource.hashid === selectedItem
      )
    );
  }

  function handleSelectItems(isChecked: boolean) {
    setSelectedItems(
      isChecked ? recycled_resources.results.map((item) => item.hashid) : []
    );
  }

  return (
    <div>
      <p className="col-xl-4 col-lg-6 col-md-9 col-sm-12">
        {gt(
          'Restore or permanently delete procedures, processes, policies and versions that were deleted by your team here.'
        )}
      </p>
      <p className="col-xl-4 col-lg-6 col-md-9 col-sm-12">
        {gt(
          'Permanently deleted procedures, processes, policies and versions are scheduled for removal from our databases and can never be restored.'
        )}
      </p>
      <RecycleBinFilterbar
        resetFilters={resetFilters}
        viewingAll={viewingAll}
      />
      <table className="table table-hover">
        <thead>
          <tr
            onClick={() =>
              handleSelectItems(
                numSelectedItems !== recycled_resources.results.length &&
                  !isIndeterminateState
              )
            }
          >
            <th>
              {recycled_resources.count > 0 && (
                <CheckboxButton
                  isChecked={
                    numSelectedItems === recycled_resources.results.length ||
                    isIndeterminateState
                  }
                  onClick={(isChecked) => handleSelectItems(isChecked)}
                  useRealCheckbox={true}
                  isIndeterminate={isIndeterminateState}
                  className="mr-2 btn btn-sm btn-link mb-1"
                />
              )}
              <span className="ml-2">{gt('Name')}</span>

              {numSelectedItems > 0 ? (
                <span className="text-muted small ml-4">
                  {gt('%(num)s selected', {num: numSelectedItems})}
                </span>
              ) : null}
            </th>
            <th>{gt('Date Deleted')}</th>
            <th>{gt('Type')}</th>
            <th>{gt('Deleted By')}</th>
            <th className="text-right" onClick={(e) => e.stopPropagation()}>
              {recycled_resources.count > 0 && (
                <div className="d-flex justify-content-end align-items-center">
                  <ActionsButton
                    actions={bulkMenuItems}
                    stopClickPropagation={true}
                  />
                </div>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {recycled_resources.results.map((recycled_resource) => (
            <RecycleRow
              recycled_resource={recycled_resource}
              is_owner={is_owner}
              setSelectedItems={setSelectedItems}
              selectedItems={selectedItems}
              key={recycled_resource.hashid}
            />
          ))}
          {recycled_resources.count === 0 &&
            (viewingAll ? (
              <EmptyBin />
            ) : (
              <EmptySearchResult query={query} resetFilters={resetFilters} />
            ))}
        </tbody>
      </table>
      <div className="mt-2">
        <LinkPaginationRTK
          page={page}
          response={recycled_resources}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

type RecycleRowProps = {
  recycled_resource: RecycledResourceSerializedType;
  is_owner: boolean;
  selectedItems: Array<string>;
  setSelectedItems: (ids: Array<string>) => unknown;
};

function RecycleRow({
  recycled_resource,
  is_owner,
  selectedItems,
  setSelectedItems,
}: RecycleRowProps) {
  const [restoreRecycledResource] = useRestoreRecycledResource();

  const [deleteRecycledResource] = useDeleteRecycledResource();

  const ct = getContentType(recycled_resource.content_type);

  const restoreDeleteRecycledResourceParams = {
    hashid: recycled_resource.hashid,
  };

  const rowMenuItems = [
    {
      text: gt('Restore'),
      key: 'restore',
      icon: 'far fa-trash-restore',
      callback: () =>
        restoreRecycledResource(restoreDeleteRecycledResourceParams),
      condition: () => true,
    },
    {
      text: gt('Permanently Delete'),
      key: 'permanently_delete',
      icon: 'far fa-trash',
      callback: () =>
        deleteRecycledResource(restoreDeleteRecycledResourceParams),
      condition: () => is_owner,
      className: 'text-danger',
    },
  ];

  function handleSelectItem(isChecked: boolean) {
    setSelectedItems(
      isChecked
        ? [...selectedItems, recycled_resource.hashid]
        : selectedItems.filter((val) => val !== recycled_resource.hashid)
    );
  }

  return (
    <tr
      onClick={() =>
        handleSelectItem(!selectedItems.includes(recycled_resource.hashid))
      }
    >
      <td className="d-flex">
        <CheckboxButton
          isChecked={selectedItems.includes(recycled_resource.hashid)}
          onClick={(isChecked) => handleSelectItem(isChecked)}
          useRealCheckbox={true}
          className="mr-3 btn btn-sm btn-link"
        />
        <RecycleNameRow
          name={recycled_resource.name}
          current_name={recycled_resource.current_name}
        />
      </td>

      <td>{moment(recycled_resource.modified_at).format('lll')}</td>
      <td>
        <i className={ct.icon}></i> {ct.name}
      </td>
      <td>{getObjectName(recycled_resource.deleted_by, 'user')}</td>
      <td onClick={(e) => e.stopPropagation()}>
        <div className="d-flex justify-content-end">
          <ActionsButton actions={rowMenuItems} stopClickPropagation={true} />
        </div>
      </td>
    </tr>
  );
}

type RecycleNameRowProps = {
  name: string;
  current_name: string;
};
function RecycleNameRow({name, current_name}: RecycleNameRowProps) {
  return (
    <div>
      <span>{current_name}</span>
      {name !== current_name && (
        <span className="small text-muted d-block">
          {gt('Was "%(name)s"', {name})}
        </span>
      )}
    </div>
  );
}

function EmptyBin() {
  return (
    <tr>
      <td colSpan={6} className="text-muted text-center">
        {gt('The recycle bin is empty')}
      </td>
    </tr>
  );
}

type EmptySearchResultProps = {
  query: DashboardLocationParamsType;
  resetFilters: () => void;
};
function EmptySearchResult({query, resetFilters}: EmptySearchResultProps) {
  return (
    <tr>
      <td colSpan={6} className="text-center">
        <h3 className="mt-4">{gt('No documents found with these filters')}</h3>
        <ResetFilterMessage
          query={query}
          ct={'document'}
          resetFilters={resetFilters}
        />
      </td>
    </tr>
  );
}
